/** @jsx jsx */
import { jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { useContext, useEffect } from 'react';
import { PanelStack } from '@blueprintjs/core';
import { ErrorBoundary } from 'components/errorBoundary';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { MainPanel } from '/views/filterHub/mainPanel';
import { isEmpty } from 'lodash-es';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { useFacetsState } from 'hooks/useFacetsState';
import { getQueryString, mergeQueryStrings } from 'views/filterHub/utils';

/**
 * This the container for the different types of property filters
 * Different filters can include: Facets, Hierarchical Facets, Date Sliders, Boolean selectors....
 * Components that change the 'filters' state are registered by this component and generate a new query.
 * @param {function} sortFacetsFunc used to apply a sort function to the facet list order, passed on to mainPanel using array sort
 * @param {array} filterPropertyProps pass array of params down to useFilteredProperties in mainPanel, will filter which facets are displayed in FilterHub
 */
export function FilterHub({
	sortFacetsFunc = null,
	filterPropertyProps = null
}) {
	const { dispatch } = useContext(WorkspaceContext);
	const { getActiveFacets, getActiveHierarchicalFacets } = useFacetsState();
	const activeFacets = getActiveFacets();
	const activeHierarchicalFacets = getActiveHierarchicalFacets();

	// update the query string when active facets update
	// generates a new activeResult that will trigger other facet components to re-render
	useEffect(() => {
		if (isEmpty(activeFacets) && isEmpty(activeHierarchicalFacets)) return;
		const facetsQueryString = getQueryString('activeFacets', activeFacets);
		const hierarchicalFacetsQueryString = getQueryString('activeHierarchicalFacets', activeHierarchicalFacets);
		dispatch(WorkspaceActionCreator.updateQueryString('facets', mergeQueryStrings([facetsQueryString, hierarchicalFacetsQueryString])));
	}, [activeFacets, activeHierarchicalFacets, dispatch]);

	return (
		<Wrapper>
			<ErrorBoundary retryButtonText={'Retry'} description={null}>
				<Wrapper>
					<PanelStack // not sure we should use this PanelStack component. Its only providing us a chunky animation...
						initialPanel={{
							component: MainPanel,
							props: {sortFacetsFunc: sortFacetsFunc, filterPropertyProps: filterPropertyProps}
						}}
						// renderActivePanelOnly={false} // true should be more performant?
						showPanelHeader={false}
					/>
				</Wrapper>
			</ErrorBoundary>
		</Wrapper>
	);
}

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;

	.bp3-panel-stack {
		height: 100%;
	}
`;
